import { useQuery } from '@apollo/client/react';
import {
  DimeAlert,
  DimeAlertAction,
  DimeAlertDescription,
  DimeAlertTitle,
  DimeButton,
  DimeSpinner,
} from '@nudool/design-system';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { TriangleAlertIcon } from 'lucide-react';

import { useBreadcrumbLabel } from '../../components/BreadcrumbLabelsContext.js';
import { assertUuidRouteParams } from '../../lib/assert-uuid-route-params.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';
import {
  GET_PART,
  type GetPartQueryResult,
  type GetPartQueryVariables,
} from '../../modules/parts/operations.js';
import { PartDetailView } from '../../modules/parts/part-detail.js';

export const Route = createFileRoute('/parts/$partId')({
  beforeLoad: ({ params }) => assertUuidRouteParams(params),
  validateSearch: (search: Record<string, unknown>) => ({
    edit: search.edit === true || search.edit === 'true',
  }),
  component: PartDetailPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function PartDetailPage() {
  const navigate = useNavigate();
  const { partId } = Route.useParams();
  const { edit: startEditing } = Route.useSearch();
  const { permissions } = usePermissions();
  // Part.brandPart is gated on brand:view while /parts only requires part:view.
  const canViewBrands = permissions.includes('brand:view');
  // Part.sellableProducts is gated on product:view while /parts only requires part:view.
  const canViewProducts = permissions.includes('product:view');
  // The five Part account relations are gated on account:view while /parts only requires part:view.
  const canViewAccounts = permissions.includes('account:view');
  // Part.taxRate is gated on taxRate:view while /parts only requires part:view.
  const canViewTaxRates = permissions.includes('taxRate:view');
  const { data, loading, error, refetch } = useQuery<GetPartQueryResult, GetPartQueryVariables>(
    GET_PART,
    {
      variables: { id: partId, canViewAccounts, canViewBrands, canViewProducts, canViewTaxRates },
    },
  );

  const part = data?.part;
  useBreadcrumbLabel(partId, part?.num);

  const setEditingInUrl = (editing: boolean) => {
    void navigate({
      to: '/parts/$partId',
      params: { partId },
      search: { edit: editing },
      replace: true,
    });
  };

  return (
    <div>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <DimeSpinner />
          Loading…
        </div>
      ) : error ? (
        // WHY: with errorPolicy 'none' a failed read leaves data undefined, which would otherwise
        // render as "Part not found." for a part that exists.
        <DimeAlert variant="destructive">
          <TriangleAlertIcon />
          <DimeAlertTitle>Could not load this part</DimeAlertTitle>
          <DimeAlertDescription>{error.message}</DimeAlertDescription>
          <DimeAlertAction>
            <DimeButton
              variant="outline"
              size="sm"
              // WHY: refetch rejects under errorPolicy 'none' and the alert already re-renders
              // from `error`, so the rejection carries nothing new.
              onClick={() => void refetch().catch(() => undefined)}
            >
              Try again
            </DimeButton>
          </DimeAlertAction>
        </DimeAlert>
      ) : (
        <PartDetailView
          key={`${partId}-${startEditing ? 'edit' : 'view'}`}
          part={part}
          initialEditing={startEditing}
          onChanged={() => void refetch()}
          onEditingChange={setEditingInUrl}
        />
      )}
    </div>
  );
}
